// src/contexts/AuthContext.js

import React, { useContext, useState, useEffect , createContext} from 'react';
import { auth } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateEmail as firebaseUpdateEmail,
  updatePassword as firebaseUpdatePassword,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification
} from 'firebase/auth';
import encryptionService from '../services/encryptionService';
import posthog from 'posthog-js';
import api from '../api/axiosConfig';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState();
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  async function signup(email, password) {
    // GPT-5 FIX: Make validation non-blocking - try to validate but don't fail signup
    try {
      const validationResponse = await api.post('/api/auth/validate-signup', { email });

      if (!validationResponse.data.allowed) {
        // Domain not allowed - trigger access denied state with waitlist info
        setAccessDenied({
          error: validationResponse.data.error,
          code: validationResponse.data.code,
          waitlist: validationResponse.data.waitlist,
          requiresInvite: validationResponse.data.requiresInvite,
          domain: validationResponse.data.domain,
          allowedDomains: validationResponse.data.allowedDomains,
          email: email
        });
        return { accessDenied: true };
      }
    } catch (error) {
      if (error.response?.status === 403) {
        // Domain restricted - show access denied state
        const errorData = error.response.data;
        setAccessDenied({
          error: errorData.error || 'Access denied',
          code: errorData.code,
          waitlist: errorData.waitlist,
          requiresInvite: errorData.requiresInvite,
          domain: errorData.domain,
          allowedDomains: errorData.allowedDomains,
          email: email
        });
        return { accessDenied: true };
      }
      // GPT-5 FIX: Don't block signup on validation errors - just log and proceed
      console.log('Pre-signup validation check failed, proceeding anyway:', error.message);
      // Continue with signup regardless
    }

    // Domain is allowed or validation failed - proceed with Firebase account creation
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Signup funnel analytics (2026-08-04): capture the moment the Firebase
    // account is created. Provider = 'password'. Referrer captured so we can
    // attribute signups to specific marketing channels (Product Hunt post,
    // subreddit thread, blog post, etc). See Landing.js for the corresponding
    // signup_started event on the CTA click side.
    try {
      posthog.capture('signup_completed', {
        provider: 'password',
        referrer: document.referrer || 'direct',
        signup_source: sessionStorage.getItem('signup_source') || 'direct',
      });
    } catch { /* posthog may not be initialized */ }

    // CRITICAL: Provision the Mongo User doc BEFORE signOut. Previously we
    // sent verification email + signed out immediately, so the backend never
    // saw a token from this user until they came back to verify + login.
    // Result: 68 password signups created Firebase accounts but had no
    // Mongo User doc. authMiddleware whitelists /verify-access for unverified
    // users (see backend/middleware/authMiddleware.js) so this call
    // succeeds even though email_verified=false; ensureUserRecord creates
    // the User doc + UserTier. Non-blocking on failure — the user can
    // still verify their email and the doc will be created on next login.
    try {
      const provisioningToken = await userCredential.user.getIdToken();
      await api.post('/api/auth/verify-access', {}, {
        headers: { Authorization: `Bearer ${provisioningToken}` }
      });
    } catch (provisionErr) {
      console.error('[AuthContext] signup provisioning failed (user still valid; will retry on next login):', provisionErr?.response?.status, provisionErr?.message);
      // Deliberately swallow — signup should not fail because of a
      // provisioning hiccup. Frontend already logs + posthog.captures
      // the same failure mode on the onAuthStateChanged path.
    }

    // CRITICAL: Send email verification before any other checks
    await sendEmailVerification(userCredential.user);

    // Immediately sign out - user must verify email first
    await signOut(auth);

    // Return success indicating verification email was sent
    return {
      success: true,
      emailVerificationSent: true,
      message: 'Please check your email and click the verification link before signing in.'
    };

    // NOTE: University access verification will happen during login after email is verified
  }

  async function login(email, password) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    // CRITICAL: Check if email is verified
    if (!userCredential.user.emailVerified) {
      await signOut(auth);
      throw new Error('Please verify your email address before signing in. Check your inbox for the verification link.');
    }

    // Now verify university access after email is confirmed
    const token = await userCredential.user.getIdToken();
    try {
      const response = await api.post(
        '/api/auth/verify-access',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.data.accessGranted) {
        await signOut(auth);
        throw new Error(response.data.message || 'Access denied. University email required.');
      }

      // Store user profile (username, etc.) — matches loginWithGoogle path
      setUserProfile(response.data.user);
    } catch (error) {
      if (error.response?.status === 403) {
        const errorData = error.response.data;
        setAccessDenied({
          error: errorData.error || 'Access denied',
          code: errorData.code,
          waitlist: errorData.waitlist,
          requiresInvite: errorData.requiresInvite,
          domain: errorData.domain,
          allowedDomains: errorData.allowedDomains,
          email: userCredential.user.email,
          userCredential,
          password
        });
        return { accessDenied: true };
      }
      await signOut(auth);
      throw error;
    }

    // Initialize encryption for verified user
    await encryptionService.initializeForUser(userCredential.user, password);

    // Signup funnel analytics — email/password login (a verified return visit).
    try {
      posthog.capture('login_completed', { provider: 'password' });
    } catch { /* posthog may not be initialized */ }

    return userCredential;
  }


  async function loginWithGoogle() {
    if (!auth) {
      throw new Error('Authentication service not available');
    }

    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      // Request hd (hosted domain) claim for university verification
      provider.setCustomParameters({
        hd: '*'
      });

      const userCredential = await signInWithPopup(auth, provider);

      // Signup funnel analytics — capture Google login. Distinguishes
      // brand-new signups from returning logins via isNewUser flag on the
      // additional user info. Referrer + signup_source captured so we can
      // attribute where the signup came from.
      try {
        const isNewUser = userCredential?._tokenResponse?.isNewUser === true;
        posthog.capture(isNewUser ? 'signup_completed' : 'login_completed', {
          provider: 'google',
          referrer: document.referrer || 'direct',
          signup_source: sessionStorage.getItem('signup_source') || 'direct',
        });
      } catch { /* posthog may not be initialized */ }

      // LOGIN: Skip domain validation - existing users should be able to login
      // Domain validation only happens during SIGNUP, not login

      // Verify university access through existing flow
      const token = await userCredential.user.getIdToken();
      try {
        const response = await api.post(
          '/api/auth/verify-access',
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (!response.data.accessGranted) {
          // Access denied - sign out and throw error
          await signOut(auth);
          throw new Error(response.data.message || 'Access denied. University email required.');
        }

        // Store user profile and onboarding status.
        // Note: showWelcomeFlow localStorage flag removed — walkthrough
        // triggering now reads userProfile.onboardingCompletedAt directly
        // from AppShell, which is stateless + backend-truth + works
        // across devices. Old flag caused Notes-tab-visit ambush for
        // months-old accounts whose flag never got cleared.
        setUserProfile(response.data.user);
      } catch (error) {
        if (error.response?.status === 403) {
          // Don't sign out immediately - show waitlist form first
          const errorData = error.response.data;
          setAccessDenied({
            error: errorData.error || 'Access denied',
            code: errorData.code,
            waitlist: errorData.waitlist,
            requiresInvite: errorData.requiresInvite,
            domain: errorData.domain,
            allowedDomains: errorData.allowedDomains,
            email: userCredential.user.email,
            userCredential
          });
          return { accessDenied: true };
        }
        // Sign out for other errors
        await signOut(auth);
        throw error;
      }

      // Initialize encryption for Google user (no password, use user data)
      await encryptionService.initializeForUser(userCredential.user);

      return userCredential;
    } catch (error) {
      throw error; // Re-throw to be handled by component
    }
  }

  function logout() {
    // Clear encryption keys on logout
    encryptionService.clearKeys();
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  function updateEmail(email) {
    return firebaseUpdateEmail(currentUser, email);
  }

  function updatePassword(password) {
    return firebaseUpdatePassword(currentUser, password);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Track auth state in localStorage so the app can show a spinner
      // instead of the landing page flash on subsequent visits.
      try {
        if (user) {
          localStorage.setItem('blackmount_auth_active', 'true');
        } else {
          localStorage.removeItem('blackmount_auth_active');
        }
      } catch { /* localStorage may be unavailable in some contexts */ }

      // Remove the CSS flash guard immediately — don't wait for profile/encryption
      try {
        document.documentElement.classList.remove('auth-loading');
      } catch { /* SSR/react-snap safe */ }

      // PostHog user identification
      if (user) {
        posthog.identify(user.uid, {
          email: user.email,
          name: user.displayName,
          provider: user.providerData[0]?.providerId || 'email'
        });
      } else {
        posthog.reset();
      }
      
      // PERF FIX: Set loading=false IMMEDIATELY so protected routes can mount.
      // verify-access and encryption run in background — profile features degrade
      // gracefully until they resolve (the code already had this comment but was
      // blocking anyway, adding ~300ms to every page load).
      setLoading(false);

      // Background: hydrate user profile (username, subscription, etc.)
      if (user) {
        user.getIdToken().then(token =>
          api.post('/api/auth/verify-access', {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ).then(async profileRes => {
          if (profileRes.data.user) {
            setUserProfile(profileRes.data.user);

            // PR 4 (iOS-app-separation): if the user has no signupFunnel yet
            // and we captured ?source= on this device, PATCH it now. Backend
            // ensureUserRecord will consume exactly once on next request.
            try {
              const profile = profileRes.data.user;
              const needsFunnel = !profile.signupFunnel || profile.signupFunnel === 'unknown';
              if (needsFunnel) {
                const { getPersistedFunnel, clearPersistedFunnel } = await import('../utils/funnelTracker');
                const pending = getPersistedFunnel();
                if (pending) {
                  const token2 = await user.getIdToken();
                  await api.patch('/api/user/profile', { signupFunnel: pending }, {
                    headers: { Authorization: `Bearer ${token2}` }
                  });
                  setUserProfile(prev => prev ? { ...prev, signupFunnel: pending } : prev);
                  clearPersistedFunnel();
                }
              }
            } catch {
              // Non-blocking — funnel attribution is best-effort.
            }
          }
        }).catch(err => {
          // Non-blocking — profile features degrade gracefully. BUT: log
          // so we can see it in the browser console + PostHog when the
          // silent-swallow was masking a real provisioning failure. Prior
          // to this we had ~30 Google/Apple accounts with valid Firebase
          // auth but no Mongo User doc; the .catch() hid the retry signal.
          console.error('[AuthContext] verify-access failed (user may lack Mongo doc):', err?.response?.status, err?.message);
          try {
            posthog.capture('verify_access_failed', {
              status: err?.response?.status,
              message: (err?.message || 'unknown').slice(0, 200),
            });
          } catch { /* posthog may not be initialized */ }
        });
      }

      // Background: initialize encryption
      if (user && !encryptionService.isReady()) {
        encryptionService.initializeForUser(user).catch(() => {});
      }
    });

    return unsubscribe;
  }, []);

  const updateUserProfile = (updates) => {
    setUserProfile(prev => prev ? { ...prev, ...updates } : updates);
  };

  const clearAccessDenied = () => {
    setAccessDenied(null);
  };

  const handleInviteSuccess = async (inviteData) => {
    if (accessDenied?.userCredential && inviteData?.inviteCode) {
      try {
        const token = await accessDenied.userCredential.user.getIdToken();

        // First, consume the invite code through the authenticated endpoint
        await api.post(
          '/api/auth/submit-invite-code',
          { inviteCode: inviteData.inviteCode },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        // Now retry the verification after invite code is consumed
        const response = await api.post(
          '/api/auth/verify-access',
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.data.accessGranted) {
          setUserProfile(response.data.user);
          setAccessDenied(null);
          // Initialize encryption with preserved password for consistency
          await encryptionService.initializeForUser(
            accessDenied.userCredential.user,
            accessDenied.password // Use preserved password if available for email/password signups
          );
          return { success: true };
        }
      } catch (error) {
        console.error('Invite code consumption or verification failed:', error);
        await signOut(auth);
        throw error;
      }
    }
  };

  const value = {
    currentUser,
    loading,
    userProfile,
    updateUserProfile,
    accessDenied,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    updateEmail,
    updatePassword,
    clearAccessDenied,
    handleInviteSuccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

